import React, { useEffect, useRef, useState } from 'react'
import { Box, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, InputAdornment, TextField } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import MDButton from 'components/MDButton';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "common/customIcons/edit";
import DeleteIcon from "common/customIcons/delete";
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { deleteHolidaySlot, fetchHolidaysList } from '../../../../redux/slices/hyperwolf/settings';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatTimestampToWithoutTime } from 'utilities/common';
import defaultImage from 'assets/images/placeholder-image.png';
import { openSnackbar } from '../../../../redux/slices/common';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../../../redux/slices/modalSlice';
import NoDataFound from 'common/component/NoDataFound';
import Loader from 'common/Loader';
import { CloseRounded } from '@mui/icons-material';
import SearchIcon from 'common/customIcons/search';
import { useDebounce } from 'hooks/useDebounce';
import { hasSectionPermission } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { getData } from 'utilities/common';

const HolidaysHome = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, holidayData } = useSelector(state => state.hyperwolfSettings);
  const { user } = useSelector(state => state.auth);
  const [searchTerm, setSearchTerm] = useState("");
  const searchTermRef = useRef('');

  let localUser = getData('login-user-info')
  if (localUser) {
    localUser = JSON.parse(localUser)
  }

  const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

  const canCreate = hasSectionPermission(permissionsForMode, "Settings", "Create");
  const canEdit = hasSectionPermission(permissionsForMode, "Settings", "Edit/Update");
  const canView = hasSectionPermission(permissionsForMode, "Settings", "View Only");
  const canDelete = hasSectionPermission(permissionsForMode, "Settings", "Delete");


  const dateWiseData = Object.entries(
    holidayData?.holiday?.reduce((acc, current) => {
      if (!acc[current.date]) {
        acc[current.date] = [];
      }
      acc[current.date].push(current);
      return acc;
    }, {}) || {}
  ).map(([date, data]) => ({
    date,
    holidays: data
  }));

  useEffect(() => {
    dispatch(fetchHolidaysList());
  }, [])


  const deleteHandler = (id) => {
    const payload = {
      updatedBy: user?.name,
    }
    dispatch(setModalLoader(true));
    dispatch(deleteHolidaySlot({ id, payload })).unwrap().then((res) => {
      showResponseMessage(res?.data?.message, 'success');
      dispatch(fetchHolidaysList());
      dispatch(closeCustomModalDialog());
    }).catch((err) => {
      dispatch(setModalLoader(false));
      showResponseMessage(err?.message, 'error');
    });

  };


  const showResponseMessage = (message, errortype) => {
    if (errortype === 'success') {
      dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
    }
    if (errortype === 'error') {
      dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
    }
  };

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
  };

  const handleSearch = useDebounce(() => {
    dispatch(fetchHolidaysList({ searchTerm: searchTermRef.current }));
  }, 500);


  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2.5} className="centralize-banner-top">
        <Typography variant='h4'>Holidays</Typography>

        <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
          <TextField
            className="custom-search-field"
            placeholder="Search"
            label=""
            value={searchTerm}
            onChange={(e) => searchHandler(e.target.value)}
            sx={{ minWidth: "280px" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton size="small">
                    <SearchIcon width="18px" height="18px" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment:
                searchTerm?.length > 0 ? (
                  <InputAdornment>
                    <IconButton onClick={() => searchHandler("")} size="small">
                      <CloseRounded />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
            style={{ minWidth: "300px" }}
          />
          {
            canView &&
            <MDButton
              variant="contained" color="primary"
              sx={{ minWidth: '160px', backgroundColor: '#2E2E3A !important', boxShadow: "none !important" }}
              style={{ textTransform: 'none', minHeight: '36px', padding: "8px 20px" }}
              onClick={() => navigate("/hyperwolf/banner-manage/activity-logs")}
            >
              Activity Logs
            </MDButton>
          }
          {
            canCreate &&
            <MDButton
              startIcon={<AddRoundedIcon fontSize='large' />}
              variant="contained" color="primary"
              sx={{ minWidth: '160px', boxShadow: "none !important" }}
              style={{ textTransform: 'none', minHeight: '36px', padding: "8px 20px" }}
              onClick={() => navigate('/hyperwolf/banner-manage/holiday-add')}
            >
              Add Graphic
            </MDButton>
          }
        </Stack>
      </Stack>

      <Box className="centralize-banner-con">
        {canView &&
          loading ? <Loader /> :
          <>
            {
              holidayData?.holiday?.length === 0 ?
                <NoDataFound />
                :
                <>
                  {
                    dateWiseData?.map((data) => {
                      return (
                        <Accordion elevation={0} key={data}>

                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" color="#2E2E3A" fontWeight="600">{formatTimestampToWithoutTime(data?.date)}</Typography>
                              <IconButton
                                color="primary"
                                disabled={!canEdit}
                                size="small"
                                variant="primary"
                                onClick={() => navigate(`/hyperwolf/banner-manage/holiday-edit-date/${data?.date}`)
                                }
                              >
                                <EditIcon width="16px" height="16px" />
                              </IconButton>
                              <IconButton
                                color="error"
                                disabled={!canDelete}
                                size="small"
                                variant="primary"
                                sx={{ marginLeft: 1 }}
                                onClick={() =>
                                  dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: { date: data?.date, type: "date" } }, modal: 'DELETE_MODAL' }))
                                }
                              >
                                <DeleteIcon width="16px" height="16px" />
                              </IconButton>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={{ p: 0 }}>
                            <TableContainer sx={{ maxHeight: 400 }}>
                              <Table stickyHeader aria-label="time slots table">
                                <TableHead sx={{ backgroundColor: "#f7f7fa" }}>
                                  <TableRow>
                                    <TableCell sx={{ color: "grey.600", fontSize: "0.75rem", py: 1 }}>Holiday Banner for Desktop</TableCell>
                                    <TableCell sx={{ color: "grey.600", fontSize: "0.75rem", py: 1 }}>Holiday Banner for Mobile</TableCell>
                                    <TableCell sx={{ color: "grey.600", fontSize: "0.75rem", py: 1 }}>Region Name</TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ color: "grey.600", fontSize: "0.75rem", py: 1 }}
                                    >
                                      Actions
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {data?.holidays.map((regionData) => {
                                    return (
                                      <TableRow key={regionData}>
                                        <TableCell>
                                          <Box style={{ width: "100px", height: "70px", borderRadius: "6px", overflow: "hidden" }}>
                                            {
                                              regionData?.desktopImages?.mediaType === 'video' ?
                                                <video
                                                  style={{
                                                    width: '100px',
                                                    height: '70px',
                                                    objectFit: 'cover',
                                                    borderRadius: '6px'
                                                  }}
                                                  controls
                                                >
                                                  <source src={regionData?.desktopImages?.imageUrl} type="video/mp4" />
                                                </video>
                                                :
                                                <img alt={regionData?.desktopImages?.altText} style={{ width: "100px", height: "70px", objectFit: "cover", borderRadius: "6px" }} src={regionData?.desktopImages?.imageUrl || defaultImage} />

                                            }
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box style={{ width: "100px", height: "70px", borderRadius: "6px", overflow: "hidden" }}>
                                            {
                                              regionData?.mobileImages?.mediaType === 'video' ?
                                                <video
                                                  style={{
                                                    width: '100px',
                                                    height: '70px',
                                                    objectFit: 'cover',
                                                    borderRadius: '6px'
                                                  }}
                                                  controls
                                                >
                                                  <source src={regionData?.mobileImages?.imageUrl} type="video/mp4" />
                                                </video>
                                                :
                                                <img alt={regionData?.mobileImages?.altText} style={{ width: "100px", height: "70px", objectFit: "cover", borderRadius: "6px" }} src={
                                                  regionData?.mobileImages?.imageUrl || defaultImage} />
                                            }
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Typography variant="body2" color="#2E2E3A">{regionData?.regionName}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Box display="flex" alignItems="center" justifyContent="flex-end" className="m-0">

                                            <IconButton
                                              color="error"
                                              disabled={!canDelete}
                                              size="small"
                                              variant="primary"
                                              sx={{ marginLeft: 1 }}
                                              onClick={() =>
                                                dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: regionData?._id }, modal: 'DELETE_MODAL' }))
                                              }
                                            >
                                              <DeleteIcon width="16px" height="16px" />
                                            </IconButton>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    )
                                  }
                                  )
                                  }
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      )
                    })
                  }
                </>
            }
          </>
        }
      </Box>

    </Box>
  )
}

export default HolidaysHome
